import { cn } from '../../utils/cn';
import type { ColumnDef, SortDirection, TableProps } from './Table.types';

function getNextSortDirection(current: SortDirection): SortDirection {
  if (current === null) return 'asc';
  if (current === 'asc') return 'desc';
  return null;
}

function SortIcon({ direction }: { direction: SortDirection }) {
  return (
    <svg
      className="ml-1 inline-block h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={
          direction === 'asc'
            ? 'M5 15l7-7 7 7'
            : direction === 'desc'
              ? 'M19 9l-7 7-7-7'
              : 'M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4'
        }
      />
    </svg>
  );
}

function SkeletonRow({ columns }: { columns: number }) {
  return (
    <tr>
      {Array.from({ length: columns }, (_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="bg-(--color-bg-secondary) h-4 animate-pulse rounded" />
        </td>
      ))}
    </tr>
  );
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  sortColumn,
  sortDirection,
  onSort,
  emptyMessage = 'No data available',
  loading = false,
  striped = false,
  className,
}: TableProps<T>) {
  const handleSort = (col: ColumnDef<T>) => {
    if (!col.sortable || !onSort) return;
    const nextDirection =
      sortColumn === col.key
        ? getNextSortDirection(sortDirection ?? null)
        : 'asc';
    onSort(col.key, nextDirection);
  };

  return (
    <div
      className={cn(
        'border-(--color-border) w-full overflow-auto rounded-lg border',
        className,
      )}
    >
      <table className="w-full border-collapse text-left text-sm" role="grid">
        <thead>
          <tr className="border-(--color-border) bg-(--color-bg-secondary) border-b">
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'text-(--color-text-secondary) px-4 py-3 font-medium',
                  col.sortable &&
                    'hover:text-(--color-text-primary) cursor-pointer select-none',
                )}
                style={col.width ? { width: col.width } : undefined}
                onClick={() => handleSort(col)}
                aria-sort={
                  sortColumn === col.key && sortDirection
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {col.header}
                {col.sortable && (
                  <SortIcon
                    direction={
                      sortColumn === col.key ? (sortDirection ?? null) : null
                    }
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }, (_, i) => (
              <SkeletonRow key={i} columns={columns.length} />
            ))
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="text-(--color-text-secondary) px-4 py-8 text-center"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={keyExtractor(row)}
                className={cn(
                  'border-(--color-border) border-b',
                  striped && rowIndex % 2 === 1 && 'bg-(--color-bg-secondary)',
                  onRowClick &&
                    'hover:bg-(--color-bg-secondary) cursor-pointer',
                )}
                onClick={() => onRowClick?.(row)}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter') onRowClick(row);
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'row' : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="text-(--color-text-primary) px-4 py-3"
                  >
                    {col.render
                      ? col.render(row)
                      : String((row as Record<string, unknown>)[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
